import React, { useState } from 'react';
import { useStore } from '../context/ProductStore';
import ProductDetail from './ProductDetail';

const ProductCard = ({ product }) => {
  const { addToCart, trackView } = useStore();
  const [added,      setAdded]      = useState(false);
  const [hovered,    setHovered]    = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);

  const images  = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const discount = product.isPromo && product.promoPrice
    ? Math.round(((product.price - product.promoPrice) / product.price) * 100) : 0;

  // Description — 2 lines when not hovered, full when hovered
  const description = product.description
    ? product.description.split('.').filter(s => s.trim()).slice(0, 3).join('. ') + '.'
    : '';

  const forTags = product.forTags?.length ? product.forTags.join(', ') : '';
  const brandTag = [forTags, product.brand].filter(Boolean).join(', ');

  const displayPrice = product.isPromo && product.promoPrice
    ? parseFloat(product.promoPrice).toFixed(2)
    : parseFloat(product.price).toFixed(2);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (product.outOfStock) return;
    addToCart(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleOpen = (e) => {
    e?.stopPropagation();
    trackView(product.id);
    setShowDetail(true);
  };

  return (
    <>
      <style>{`
        @keyframes td-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(204,0,0,0.18)} 50%{box-shadow:0 0 0 6px rgba(204,0,0,0)} }
        .td-card {
          background: #fff;
          border: 1px solid #e8e8e8;
          display: flex;
          flex-direction: column;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
        }
        .td-card:hover {
          box-shadow: 0 16px 48px rgba(0,0,0,0.14);
          transform: translateY(-6px);
          border-color: #e0e0e0;
          z-index: 2;
        }
        /* Image zooms OUT (scale down from 1.08 to 1) giving a slow retreating feel */
        .td-img {
          max-width: 85%;
          max-height: 85%;
          object-fit: contain;
          display: block;
          transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          transform: scale(1.08);
          will-change: transform;
        }
        .td-card:hover .td-img {
          transform: scale(1.0);
        }
        /* Description: clamp 2 lines when not hovered */
        .td-desc {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
          font-family: 'Inter', sans-serif;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          -webkit-line-clamp: 2;
          transition: -webkit-line-clamp 0.3s;
        }
        /* Action bar — always visible at bottom of card, no slide */
        .td-action-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          padding: 10px 12px 14px;
          border-top: 1px solid #f0f0f0;
          margin-top: 4px;
        }
        .td-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 7px 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .td-icon-btn:hover { transform: scale(1.2); }
        .td-icon-btn:hover svg { stroke: #CC0000 !important; }
        .td-read-btn {
          flex: 1;
          padding: 10px 20px;
          border: none;
          border-radius: 100px;
          background: #1a73e8;
          color: #fff;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.06em;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: background 0.2s;
          white-space: nowrap;
          margin: 0 8px;
        }
        .td-read-btn:hover { background: #CC0000; }
        .td-name {
          font-size: 15px;
          font-weight: 700;
          color: #111;
          line-height: 1.4;
          margin: 0;
          font-family: 'League Spartan', sans-serif;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-align: center;
        }
        .td-brand {
          font-size: 11px;
          font-weight: 500;
          color: #9ca3af;
          text-align: center;
          font-family: 'Inter', sans-serif;
          letter-spacing: 0.04em;
        }
        /* Compare shuffle icon top-right on hover */
        .td-shuffle {
          position: absolute;
          top: 10px; right: 10px;
          opacity: 0;
          transition: opacity 0.25s;
          z-index: 5;
        }
        .td-card:hover .td-shuffle { opacity: 1; }
      `}</style>

      <div
        className="td-card"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* ── IMAGE AREA ── */}
        <div style={S.imgBox} onClick={handleOpen}>
          {images.length > 0 ? (
            <img src={images[0]} alt={product.name} className="td-img" loading="lazy" />
          ) : (
            <div style={S.noImg}>📦</div>
          )}

          {/* Badges */}
          {product.outOfStock && <div style={S.outBadge}>Out of Stock</div>}
          {product.isPromo && !product.outOfStock && (
            <div style={S.promoBadge}>SAVE {discount}%</div>
          )}

          {/* ── Slide-up action bar removed from here ── */}
        </div>

        {/* Shuffle/compare icon row — always visible, right-aligned, like picture 2 */}
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'6px 12px 0', borderTop:'1px solid #f3f4f6' }}>
          <button onClick={e => e.stopPropagation()} style={{ background:'none', border:'none', cursor:'pointer', padding:4, opacity:0.5 }} title="Compare">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
              <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
            </svg>
          </button>
        </div>
        <div style={S.info} onClick={handleOpen}>

          {/* Name */}
          <h3 className="td-name">{product.name}</h3>

          {/* Brand / tags */}
          {brandTag && <div className="td-brand">{brandTag}</div>}

          {/* Description — 2-line clamp, expands slightly on hover via CSS */}
          {description && (
            <p className="td-desc">{description}</p>
          )}

          {/* Price */}
          <div style={S.priceRow}>
            {product.isPromo && product.promoPrice ? (
              <>
                <span style={S.mainPrice}>USD {displayPrice}</span>
                <span style={S.oldPrice}>USD {parseFloat(product.price).toFixed(2)}</span>
              </>
            ) : (
              <span style={S.mainPrice}>USD {displayPrice}</span>
            )}
          </div>
        </div>

        {/* ── Action bar — always at bottom ── */}
        <div className="td-action-bar">
          <button
            className="td-icon-btn"
            onClick={e => { e.stopPropagation(); setWishlisted(v => !v); }}
            title="Wishlist"
          >
            <svg width="19" height="19" viewBox="0 0 24 24"
              fill={wishlisted ? '#CC0000' : 'none'}
              stroke={wishlisted ? '#CC0000' : '#666'}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>

          <button
            className="td-read-btn"
            onClick={e => { e.stopPropagation(); handleOpen(); }}
            style={added ? { background:'#16a34a' } : {}}
          >
            {added ? '✓ ADDED!' : 'READ MORE'}
          </button>

          <button
            className="td-icon-btn"
            onClick={e => { e.stopPropagation(); handleOpen(); }}
            title="Quick View"
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
        </div>
      </div>

      {showDetail && (
        <ProductDetail product={product} onClose={() => setShowDetail(false)} />
      )}
    </>
  );
};

const S = {
  imgBox: {
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 24px 20px',
    position: 'relative',
    aspectRatio: '4/3',
    overflow: 'hidden',
  },

  noImg: { fontSize: 56 },

  outBadge:   { position:'absolute', top:10, left:10, background:'#374151', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:100 },
  promoBadge: { position:'absolute', top:10, left:10, background:'#CC0000', color:'#fff', fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:100 },

  shuffleBtn: {
    background: 'rgba(255,255,255,0.9)',
    border: '1px solid #e5e7eb',
    borderRadius: '50%',
    width: 30, height: 30,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },

  info: {
    padding: '16px 16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    cursor: 'pointer',
  },

  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 6,
  },

  mainPrice: {
    fontSize: 18,
    fontWeight: 900,
    color: '#111',
    fontFamily: "'League Spartan', sans-serif",
    letterSpacing: '-0.01em',
  },

  oldPrice: {
    fontSize: 13,
    color: '#9ca3af',
    textDecoration: 'line-through',
    fontFamily: "'League Spartan', sans-serif",
  },
};

export default ProductCard;
